import React, { useEffect, useRef } from "react";
import { animate } from "animejs";

import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrushIcon from "@mui/icons-material/Brush";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Car3D from "./components/Car3D";


// ----------- Hook: scroll-based animation with Anime.js ----------
const useAnimeOnInView = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animate(entry.target, {
              translateY: [50, 0],
              opacity: [0, 1],
              duration: options.duration || 1000,
              easing: options.easing || "easeOutExpo",
              delay: options.delay || 0,
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.duration, options.easing, options.delay]);

  return ref;
};


// --------------------------- Hero Section -----------------------------------
const HeroSection = () => {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const bottleRef = useRef(null);

  useEffect(() => {
    if (titleRef.current) {
      animate(titleRef.current, {
        translateY: [40, 0],
        opacity: [0, 1],
        duration: 1200,
        easing: "easeOutExpo",
      });
    }

    if (subtitleRef.current) {
      animate(subtitleRef.current, {
        translateY: [40, 0],
        opacity: [0, 1],
        delay: 200,
        duration: 1200,
        easing: "easeOutExpo",
      });
    }

    if (bottleRef.current) {
      // first pop-in
      animate(bottleRef.current, {
        rotateY: ["0deg", "25deg"],
        translateY: [10, 0],
        scale: [0.9, 1],
        duration: 1500,
        easing: "easeOutElastic(1, 0.8)",
      });

      // subtle looping sway
      animate(bottleRef.current, {
        rotateY: ["-10deg", "10deg"],
        direction: "alternate",
        loop: true,
        easing: "easeInOutSine",
        duration: 3000,
      });
    }
  }, []);

  return (
    <Box
      component="section"
      id="home"
      className="min-h-screen flex items-center relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
    >
      {/* Background SVG waves */}
      <Box className="pointer-events-none absolute inset-0 opacity-30">
        <svg
          className="absolute -top-10 left-0 w-[140%]"
          viewBox="0 0 1440 320"
        >
          <path
            fill="#0ea5e9"
            fillOpacity="0.3"
            d="M0,224L48,218.7C96,213,192,203,288,181.3C384,160,480,128,576,117.3C672,107,768,117,864,144C960,171,1056,213,1152,234.7C1248,256,1344,256,1392,256L1440,256L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
          ></path>
        </svg>
      </Box>

      <Container maxWidth="lg" className="relative z-10">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box className="space-y-6">
              <Typography
                ref={titleRef}
                variant="h2"
                className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-sky-300 drop-shadow-lg"
              >
                Blue Label
                <span className="block text-sky-100">
                  Custom Water Bottles,{" "}
                  <span className="text-sky-400">Your Brand. Your Story.</span>
                </span>
              </Typography>

              <Typography
                ref={subtitleRef}
                variant="h6"
                className="text-slate-200/90 max-w-xl"
              >
                We create premium custom-labeled water bottles for events,
                businesses, and brands. Add your logo, colors, and message on
                every sip.
              </Typography>

              <Box className="flex flex-wrap gap-4 pt-2">
                <Button
                  variant="contained"
                  size="large"
                  className="!bg-sky-500 hover:!bg-sky-400 !text-slate-950 !font-semibold !px-8 !py-3 !rounded-full shadow-lg shadow-sky-500/30"
                  href="#contact"
                >
                  Get a Quote
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  className="!border-sky-400 !text-sky-300 hover:!bg-sky-900/40 !rounded-full !px-8 !py-3"
                  href="#features"
                >
                  See Features
                </Button>
              </Box>

              <Box className="flex items-center gap-3 text-sky-200/80 pt-4">
                <AutoAwesomeIcon fontSize="small" />
                <Typography variant="body2">
                  Low minimum order • Fast delivery • Full-color printing
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 3D style bottle card */}
          <Grid item xs={12} md={5} className="flex justify-center">
            <Box
              ref={bottleRef}
              className="relative w-64 h-80 md:w-72 md:h-96 [perspective:1000px]"
            >
              <Box className="absolute inset-0 rounded-3xl bg-gradient-to-b from-sky-500/30 via-slate-900 to-slate-950 border border-sky-500/40 shadow-2xl shadow-sky-500/30 flex flex-col items-center justify-center transform-gpu hover:-rotate-3 hover:scale-105 transition-transform duration-500">
                <Box className="w-24 h-24 rounded-full bg-sky-400/20 border border-sky-400/50 flex items-center justify-center mb-4 backdrop-blur">
                  <LocalDrinkIcon className="text-sky-200" fontSize="large" />
                </Box>
                <Typography
                  variant="h5"
                  className="text-sky-100 font-semibold tracking-wide"
                >
                  Your Label Here
                </Typography>
                <Typography
                  variant="body2"
                  className="text-sky-100/80 mt-2 text-center px-6"
                >
                  Upload your logo & branding to create a unique Blue Label
                  bottle your customers will remember.
                </Typography>

                {/* Simulated bottle silhouette */}
                <Box className="mt-6 w-20 h-40 bg-gradient-to-b from-sky-200/90 to-sky-500/60 rounded-[40px] relative overflow-hidden shadow-lg shadow-sky-300/40">
                  <Box className="absolute inset-x-2 top-3 h-24 bg-sky-900/70 rounded-2xl border border-sky-200/50 flex items-center justify-center text-[10px] font-semibold text-sky-100 text-center px-2">
                    BLUE LABEL <br /> CUSTOM BRAND
                  </Box>
                  <Box className="absolute inset-x-4 bottom-4 h-3 bg-sky-100/80 rounded-full blur-[3px]" />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};


// --------------------------- Features Section -------------------------------
const FeaturesSection = () => {
  const sectionRef = useAnimeOnInView();

  const features = [
    {
      icon: <BrushIcon className="text-sky-500" />,
      title: "Fully Custom Label",
      desc: "Logos, colors, QR codes, event details – we print your design edge-to-edge.",
    },
    {
      icon: <LocalDrinkIcon className="text-sky-500" />,
      title: "Premium Water",
      desc: "Crystal-clear, refreshing water ideal for events, hotels, offices, and branding.",
    },
    {
      icon: <AutoAwesomeIcon className="text-sky-500" />,
      title: "Low MOQ & Fast Delivery",
      desc: "Perfect for both small and large orders, delivered on time, every time.",
    },
  ];

  return (
    <Box
      component="section"
      id="features"
      ref={sectionRef}
      className="py-20 bg-slate-950"
    >
      <Container maxWidth="lg">
        <Box className="text-center mb-12">
          <Typography
            variant="h4"
            className="text-3xl md:text-4xl font-bold text-sky-300"
          >
            Why Blue Label Bottles?
          </Typography>
          <Typography
            variant="body1"
            className="text-slate-300/80 max-w-2xl mx-auto mt-3"
          >
            Make every bottle a moving billboard for your brand. From events to
            corporate gifting, we help you stand out.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((f, idx) => (
            <Grid key={idx} item xs={12} md={4}>
              <Paper className="h-full bg-slate-900/70 border border-sky-500/20 rounded-3xl p-6 flex flex-col gap-3 hover:translate-y-[-4px] hover:border-sky-400/60 transition-all duration-300">
                <Box className="w-12 h-12 rounded-2xl bg-sky-500/10 flex items-center justify-center mb-1">
                  {f.icon}
                </Box>
                <Typography
                  variant="h6"
                  className="text-sky-500 font-semibold"
                >
                  {f.title}
                </Typography>
                <Typography variant="body2" className="text-slate-500">
                  {f.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};


// ----------------------- 3D Cards / Scroll Area ----------------------------
const BottlesShowcaseSection = () => {
  const sectionRef = useAnimeOnInView({ duration: 1200 });

  const bottles = [
    {
      label: "Event Edition",
      desc: "Perfect for weddings, conferences, concerts & launches.",
      bg: "from-sky-500/60 to-sky-900/80",
    },
    {
      label: "Corporate Pack",
      desc: "Office branding, client meetings, and reception desks.",
      bg: "from-sky-400/70 to-blue-900/80",
    },
    {
      label: "Limited Series",
      desc: "Seasonal campaigns & special promotions with unique designs.",
      bg: "from-cyan-400/70 to-sky-900/90",
    },
  ];

  return (
    <Box
      component="section"
      id="showcase"
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"
    >
      <Container maxWidth="lg">
       

        <Box className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 mb-10">

          <Box>
            <Typography variant="h4" className="text-3xl md:text-4xl font-bold text-sky-300">
              3D Bottle Showcase
            </Typography>

            <Typography variant="body1" className="text-slate-300/85 mt-3 max-w-xl">
              Scroll and hover to see how your Blue Label bottles can look in
              different scenarios – events, offices, and campaigns.
            </Typography>
          </Box>

          {/* RIGHT SIDE — 3D VIEWER */}
          <Box
            className="rounded-xl"
            sx={{
              width: { xs: "100%", md: 400 },
              height: 400,
              backgroundColor: "#0b0b0b",
              overflow: "hidden"
            }}
          >
            <Car3D />
          </Box>

        </Box>


        <Grid container spacing={4} className="transform-gpu">
          {bottles.map((b, idx) => (
            <Grid key={idx} item xs={12} md={4}>
              <Box className="group [perspective:1200px] h-full">
                <Box
                  className={`relative h-72 rounded-3xl bg-gradient-to-b ${b.bg} border border-sky-100/30 shadow-2xl shadow-sky-500/40 overflow-hidden transform-gpu transition-transform duration-500 group-hover:rotate-x-6 group-hover:-rotate-y-6 group-hover:scale-105`}
                >
                  {/* Fake bottle */}
                  <Box className="absolute left-1/2 -translate-x-1/2 bottom-4 w-20 h-40 bg-gradient-to-b from-sky-100 to-sky-500 rounded-[40px] shadow-xl shadow-sky-900/60">
                    <Box className="absolute inset-x-3 top-4 h-16 bg-sky-900/80 rounded-2xl border border-sky-100/60 text-[10px] text-center text-sky-100 flex items-center justify-center px-1 font-semibold">
                      BLUE LABEL
                      <br />
                      {b.label.toUpperCase()}
                    </Box>
                    <Box className="absolute inset-x-4 top-2 h-2 bg-sky-200/80 rounded-full blur-[2px]" />
                    <Box className="absolute inset-x-5 bottom-3 h-3 bg-sky-100/70 rounded-full blur-[4px]" />
                  </Box>

                  {/* Text */}
                  <Box className="absolute top-5 left-5 right-5">
                    <Typography
                      variant="subtitle1"
                      className="text-slate-950/80 font-bold tracking-wide"
                    >
                      {b.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      className="text-slate-900/80 mt-1"
                    >
                      {b.desc}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};


// --------------------------- Contact Section --------------------------------
const ContactSection = () => {
  const sectionRef = useAnimeOnInView({ duration: 1100 });

  const handleSubmit = e => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const payload = Object.fromEntries(data.entries());
    console.log("Contact form data:", payload);
    alert("Thank you! We’ll contact you soon about Blue Label bottles.");
  };

  return (
    <Box
      component="section"
      id="contact"
      ref={sectionRef}
      className="py-20 bg-slate-950"
    >
      <Container maxWidth="md">
        <Box className="text-center mb-10">
          <Typography
            variant="h4"
            className="text-3xl md:text-4xl font-bold text-sky-300"
          >
            Get Your Custom Blue Label Quote
          </Typography>
          <Typography
            variant="body1"
            className="text-slate-300/85 mt-3"
          >
            Share your requirements and we’ll get back with pricing, designs,
            and timelines.
          </Typography>
        </Box>

        <Paper className="bg-slate-900/80 border border-sky-500/30 rounded-3xl p-6 md:p-8 shadow-xl shadow-sky-900/60">
          <Box
            component="form"
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Your Name"
                  fullWidth
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="phone"
                  label="Phone / WhatsApp"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="quantity"
                  label="Approx. Quantity (e.g. 200 bottles)"
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="message"
                  label="Tell us about your event / brand"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Box className="flex justify-between items-center pt-4 gap-4 flex-col md:flex-row">
              <Typography variant="body2" className="text-slate-500">
                We usually respond within{" "}
                <span className="text-sky-300 font-semibold">
                  24 business hours.
                </span>
              </Typography>
              <Button
                type="submit"
                variant="contained"
                size="large"
                className="!bg-sky-500 hover:!bg-sky-400 !text-slate-950 !font-semibold !px-8 !py-2.5 !rounded-full"
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};


// ------------------------------ Footer --------------------------------------
const FooterSection = () => {
  return (
    <Box component="footer" className="py-6 bg-slate-950 border-t border-slate-800">
      <Container maxWidth="lg">
        <Box className="flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-slate-400">
          <Typography variant="body2">
            © {new Date().getFullYear()} Blue Label. All rights reserved.
          </Typography>
          <Typography variant="body2">
            Custom-labeled bottled water for brands, events & businesses.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};


// --------------------- Floating WhatsApp Button -----------------------------
const FloatingWhatsApp = () => {
  const btnRef = useRef(null);

  useEffect(() => {
    if (!btnRef.current) return;

    animate(btnRef.current, {
      scale: [0, 1],
      duration: 800,
      easing: "easeOutElastic(1, 0.6)",
    });

    animate(btnRef.current, {
      scale: [1, 1.1],
      direction: "alternate",
      loop: true,
      easing: "easeInOutSine",
      duration: 1200,
    });
  }, []);

  // put your own real WhatsApp number here
  const whatsappNumber = "+917974607350";
  const defaultMsg =
    "Hi Blue Label, I want custom water bottles with my own label.";

  return (
    <Box
      ref={btnRef}
      className="fixed left-4 bottom-6 z-50"
    >
      <a
        href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
          defaultMsg
        )}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <IconButton
          size="large"
          className="!bg-green-500 hover:!bg-green-400 shadow-xl shadow-green-500/50 !w-14 !h-14"
        >
          <WhatsAppIcon className="!text-white !text-3xl" />
        </IconButton>
      </a>
    </Box>
  );
};


// ------------------------------- MAIN APP -----------------------------------
const App = () => {
  return (
    <Box className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      <HeroSection />
      <FeaturesSection />
      <BottlesShowcaseSection />
      <ContactSection />
      <FooterSection />
      <FloatingWhatsApp />
    </Box>
  );
};

export default App;
